import { useState, type CSSProperties } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  Check, AlertOctagon, AlertTriangle, Info, Filter, Columns3,
  Rows3, Download, Calendar, ArrowUpRight, ArrowDownRight,
  ListChecks,
} from 'lucide-react'

// ─── Category palette (page-local, OKLCH per design rules) ───────────────────

const CAT_COLORS = {
  time:     'var(--ns-brand-strong)',
  contract: 'oklch(0.55 0.16 258)',
  product:  'oklch(0.62 0.13 50)',
  license:  'oklch(0.55 0.15 295)',
} as const

type CategoryKey = keyof typeof CAT_COLORS

const CAT_LABELS: Record<CategoryKey, string> = {
  time:     'Time',
  contract: 'Contract',
  product:  'Clementine',
  license:  'TX + Azure',
}

// ─── Mock data (sourced from design — replaced by API in follow-up WIs) ──────
//
// Numbers and customer rows below are placeholder values that match the
// Invoicing.html design hand-off so layout/typography can be reviewed.
// They will be wired to real backend endpoints in a follow-up WI alongside the
// project-filtered Composer deep-links.

const RUN = {
  monthLabel:     'May 2026',
  workingDay:     10,
  workingDays:    21,
  closeDate:      'Fri May 29',
  daysToClose:    11,
  runWindow:      'May 14 → May 29',
  targetKsek:     3200,
  invoicedKsek:   2085,
  leftKsek:       1115,
  pendingInvoices: 15,
  customersAction: 9,
  redFlags:       2,
  draftsInReview: 3,
  paceDelta:      8,
}

const CATEGORIES: { key: CategoryKey; label: string; sub: string; pct: number; invoiced: number; target: number; left: number; invoices: number; delta: number }[] = [
  { key: 'time',     label: 'Time-based',         sub: 'hours × rate',     pct: 69, invoiced:  970, target: 1400, left: 430, invoices: 7, delta:  6 },
  { key: 'contract', label: 'Service agreements', sub: 'monthly fee',      pct: 70, invoiced:  560, target:  800, left: 240, invoices: 4, delta:  2 },
  { key: 'product',  label: 'Clementine',         sub: 'product',          pct: 51, invoiced:  180, target:  350, left: 170, invoices: 3, delta: -4 },
  { key: 'license',  label: 'License resale',     sub: 'TX + Azure',       pct: 58, invoiced:  375, target:  650, left: 275, invoices: 1, delta: 12 },
]

const PMS = [
  {
    initials: 'HB', name: 'Henrik Berg', av: 'linear-gradient(135deg, #2D6CDF, #7B5BD9)',
    customers: 4, invoices: 6, flagged: 0, leftKsek: 390, totalKsek: 1040, invoicedKsek: 650,
    segments: { time: 24.0, contract: 12.5, product: 0, license: 11.5 },
    remaining: { time: 13.9, contract:  6.7, product: 8.2, license:  8.7 },
  },
  {
    initials: 'AL', name: 'Anna Lindberg', av: 'linear-gradient(135deg, #c9663e, #7b5bd9)',
    customers: 3, invoices: 4, flagged: 1, leftKsek: 240, totalKsek: 645, invoicedKsek: 405,
    segments: { time: 31.0, contract: 14.0, product: 0, license: 17.8 },
    remaining: { time: 15.5, contract:  3.1, product: 0, license: 18.6 },
  },
  {
    initials: 'PE', name: 'Per Edlund', av: 'linear-gradient(135deg, #B5476B, #C9663E)',
    customers: 3, invoices: 3, flagged: 0, leftKsek: 235, totalKsek: 760, invoicedKsek: 525,
    segments: { time: 28.9, contract: 13.2, product: 0, license: 27.0 },
    remaining: { time: 17.8, contract:  5.3, product: 0, license:  7.8 },
  },
  {
    initials: 'MS', name: 'Mikael Söderberg', av: 'linear-gradient(135deg, #4a8a3a, #3F8A8A)',
    customers: 3, invoices: 4, flagged: 1, leftKsek: 250, totalKsek: 755, invoicedKsek: 505,
    segments: { time: 14.6, contract:  9.3, product: 6.6, license: 35.8 },
    remaining: { time: 11.9, contract:  9.3, product: 7.9, license:  4.6 },
  },
]

type FlagSeverity = 'danger' | 'warn' | 'info'

const FLAGS: { severity: FlagSeverity; title: string; body: string; secondary: string; action: string }[] = [
  { severity: 'danger', title: 'Trustly',     body: 'active MSA, no hours registered in May',                  secondary: 'Apr: 110 kSEK · May: 0 kSEK · PM Anna Lindberg',          action: 'Investigate →' },
  { severity: 'danger', title: 'Storytel',    body: 'contract not closed, no invoice for 3 months',            secondary: 'Last invoice: Feb 2026 · 60 kSEK · PM Mikael Söderberg',  action: 'Close or bill →' },
  { severity: 'warn',   title: 'Klarna',      body: 'hours dropped −42% vs April',                             secondary: 'Apr: 220 kSEK · May: 128 kSEK · check scope or budget',    action: 'Check →' },
  { severity: 'warn',   title: 'Polestar',    body: 'TimeXtender license renewal overdue',                     secondary: 'Renewed Mar 1, 2026 · invoice not yet raised · 25 kSEK',   action: 'Raise →' },
  { severity: 'info',   title: 'Electrolux',  body: 'Clementine usage up +18% — check tier',                   secondary: 'May overage: 1 200 events · standard tier cap 1 000',      action: 'Review tier →' },
  { severity: 'info',   title: '3 customers', body: 'not yet billed this run — usually invoiced by 14th',      secondary: 'SEB · Telia · Ericsson — drafts ready, awaiting PM sign-off', action: 'Open drafts →' },
]

type Status = 'ready' | 'review' | 'sent' | 'flagged' | 'hold' | 'tier-check'

interface Row {
  customer:    string
  customerKey: string
  ph:          string  // background gradient
  phColor?:    string  // text color override
  project:     string
  pm:          { initials: string; name: string; av: string }
  categories:  CategoryKey[]
  aprKsek:     number
  mayKsek:     number
  deltaPct:    number  // positive = up, negative = down, 0 = flat
  status:      Status
  statusLabel: string
  action:      string
  primary?:    boolean
  flagged?:    boolean
  noteRow?:    string
}

const PM_HB = { initials: 'HB', name: 'Henrik Berg',       av: 'linear-gradient(135deg, #2D6CDF, #7B5BD9)' }
const PM_AL = { initials: 'AL', name: 'Anna Lindberg',     av: 'linear-gradient(135deg, #c9663e, #7b5bd9)' }
const PM_PE = { initials: 'PE', name: 'Per Edlund',        av: 'linear-gradient(135deg, #B5476B, #C9663E)' }
const PM_MS = { initials: 'MS', name: 'Mikael Söderberg',  av: 'linear-gradient(135deg, #4a8a3a, #3F8A8A)' }

const WORKLIST: Row[] = [
  { customer: 'IKEA Retail',  customerKey: 'IK', ph: 'linear-gradient(135deg, #FBD914, #FFC107)', phColor: '#0058A3', project: 'DW Migration · BI Dashboards', pm: PM_HB, categories: ['time', 'contract', 'product', 'license'], aprKsek: 580, mayKsek: 635, deltaPct: 9.5,  status: 'ready',     statusLabel: 'Ready',           action: 'Send →',          primary: true },
  { customer: 'Volvo Cars',   customerKey: 'VC', ph: 'linear-gradient(135deg, #1a1a1a, #404040)', project: 'EV Analytics Platform',          pm: PM_PE, categories: ['time', 'contract', 'license'],            aprKsek: 245, mayKsek: 280, deltaPct: 14,   status: 'ready',     statusLabel: 'Ready',           action: 'Send →',          primary: true },
  { customer: 'H&M Group',    customerKey: 'HM', ph: 'linear-gradient(135deg, #E50010, #B5000C)', project: 'Customer Data Platform',         pm: PM_MS, categories: ['time', 'contract', 'product'],            aprKsek: 195, mayKsek: 205, deltaPct: 5,    status: 'ready',     statusLabel: 'Ready',           action: 'Send →',          primary: true },
  { customer: 'Scania',       customerKey: 'SC', ph: 'linear-gradient(135deg, #041E42, #1B3870)', project: 'Finance Reporting',              pm: PM_HB, categories: ['time', 'contract'],                       aprKsek: 185, mayKsek: 195, deltaPct: 5,    status: 'review',    statusLabel: 'In review',       action: 'Open draft →' },
  { customer: 'Spotify',      customerKey: 'SP', ph: 'linear-gradient(135deg, #1DB954, #128A3A)', project: 'Marketing Analytics',            pm: PM_AL, categories: ['time', 'contract'],                       aprKsek: 155, mayKsek: 165, deltaPct: 6.5,  status: 'sent',      statusLabel: 'Sent · May 12',   action: 'View →' },
  { customer: 'Electrolux',   customerKey: 'EX', ph: 'linear-gradient(135deg, #003B71, #0066B2)', project: 'Supply Chain Insights',          pm: PM_MS, categories: ['time', 'product', 'license'],             aprKsek: 175, mayKsek: 205, deltaPct: 17,   status: 'tier-check', statusLabel: 'Tier check',     action: 'Review tier →' },
  { customer: 'Telia',        customerKey: 'TE', ph: 'linear-gradient(135deg, #990AE3, #5D0788)', project: 'Network Analytics',              pm: PM_AL, categories: ['time', 'license'],                        aprKsek: 175, mayKsek: 190, deltaPct: 8.5,  status: 'review',    statusLabel: 'In review',       action: 'Open draft →' },
  { customer: 'SEB',          customerKey: 'SE', ph: 'linear-gradient(135deg, #2D6CDF, #1B4FB3)', project: 'Risk Modelling',                 pm: PM_HB, categories: ['time', 'contract', 'product'],            aprKsek: 160, mayKsek: 170, deltaPct: 6,    status: 'review',    statusLabel: 'In review',       action: 'Open draft →' },
  { customer: 'Klarna',       customerKey: 'KL', ph: 'linear-gradient(135deg, #FFB3C7, #F26C8C)', phColor: '#1a1f1f', project: 'Fraud Detection',                pm: PM_PE, categories: ['time', 'contract'],                       aprKsek: 220, mayKsek: 128, deltaPct: -42,  status: 'review',    statusLabel: 'Check scope',     action: 'Investigate →' },
  { customer: 'Polestar',     customerKey: 'PO', ph: 'linear-gradient(135deg, #C8C9CA, #8A8C8E)', phColor: '#1a1f1f', project: 'EV Telemetry · TX renewal',      pm: PM_HB, categories: ['time', 'contract', 'license'],            aprKsek:  85, mayKsek: 115, deltaPct: 35,   status: 'review',    statusLabel: 'License overdue', action: 'Raise TX →' },
  { customer: 'Ericsson',     customerKey: 'ER', ph: 'linear-gradient(135deg, #0066CC, #003F7F)', project: '5G Performance · Azure',         pm: PM_PE, categories: ['time', 'license'],                        aprKsek:  88, mayKsek:  95, deltaPct: 8,    status: 'ready',     statusLabel: 'Ready',           action: 'Send →',          primary: true },
  { customer: 'Trustly',      customerKey: 'TR', ph: 'linear-gradient(135deg, #0AAC7C, #048459)', project: 'Active MSA · no projects logged', pm: PM_AL, categories: ['contract'],                              aprKsek: 110, mayKsek:   0, deltaPct: -100, status: 'hold',      statusLabel: 'No hours',        action: 'Contact PM →',    flagged: true },
  { customer: 'Storytel',     customerKey: 'ST', ph: 'linear-gradient(135deg, #FE5135, #C13E26)', project: 'Dormant · contract not closed',  pm: PM_MS, categories: ['contract'],                              aprKsek:   0, mayKsek:   0, deltaPct: 0,    status: 'hold',      statusLabel: 'No invoice 3 mo', action: 'Close or bill →', flagged: true, noteRow: '— · 3 mo' },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtKsek(n: number): string {
  return n.toLocaleString('sv-SE').replace(/,/g, ' ')
}

function pctOf(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0
}

// ─── Reusable bits ────────────────────────────────────────────────────────────

function CatPill({ cat }: { cat: CategoryKey }) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 8px 3px 7px',
        borderRadius: 999,
        background: 'var(--ns-surface-3)',
        fontSize: 11, fontWeight: 600,
        color: 'var(--ns-text-muted)',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: CAT_COLORS[cat] }} />
      {CAT_LABELS[cat]}
    </span>
  )
}

function StatusPill({ status, label }: { status: Status; label: string }) {
  const palette: Record<Status, CSSProperties> = {
    ready:        { background: 'var(--ns-ok-soft)',    color: 'var(--ns-ok)' },
    review:       { background: 'var(--ns-warn-soft)',  color: 'oklch(from var(--ns-warn) calc(l - 0.1) c h)' },
    'tier-check': { background: 'var(--ns-surface-3)',  color: 'var(--ns-text-muted)' },
    hold:         { background: 'var(--ns-err-soft)',   color: 'var(--ns-err)' },
    sent:         { background: 'var(--ns-brand-soft)', color: 'var(--ns-brand-strong)' },
    flagged:      { background: 'var(--ns-err-soft)',   color: 'var(--ns-err)' },
  }
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 999,
        fontSize: 10.5, fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
        ...palette[status],
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
      {label}
    </span>
  )
}

function DeltaCell({ pct }: { pct: number }) {
  if (pct === 0) {
    return (
      <span style={{ color: 'var(--ns-text-muted)', fontSize: 11.5, fontWeight: 600 }}>
        — · flat
      </span>
    )
  }
  const up = pct > 0
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      color: up ? 'var(--ns-ok)' : 'var(--ns-err)',
      fontSize: 11.5, fontWeight: 600,
    }}>
      {up ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
      {Math.abs(pct)}%
    </span>
  )
}

function Checkbox({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <span
      onClick={onClick}
      style={{
        display: 'inline-block', width: 16, height: 16,
        border: `1.5px solid ${on ? 'var(--ns-brand-strong)' : 'var(--ns-border-strong)'}`,
        borderRadius: 3,
        background: on ? 'var(--ns-brand-strong)' : 'var(--ns-surface)',
        cursor: 'pointer', position: 'relative', verticalAlign: 'middle',
      }}
    >
      {on && (
        <span style={{
          position: 'absolute', left: 4, top: 1,
          width: 4, height: 8,
          borderStyle: 'solid', borderColor: 'white',
          borderWidth: '0 2px 2px 0',
          transform: 'rotate(45deg)',
        }} />
      )}
    </span>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const WORKLIST_TABS = [
  { key: 'all',     label: 'All',           count: WORKLIST.length },
  { key: 'ready',   label: 'Ready to send', count: WORKLIST.filter(r => r.status === 'ready').length },
  { key: 'review',  label: 'In review',     count: WORKLIST.filter(r => r.status === 'review').length },
  { key: 'hold',    label: 'On hold',       count: WORKLIST.filter(r => r.status === 'hold').length },
  { key: 'flagged', label: 'Flagged',       count: WORKLIST.filter(r => r.flagged).length, danger: true },
] as const

type WorklistTab = typeof WORKLIST_TABS[number]['key']

const SEG_PERIODS = ['Mar', 'Apr', 'May', 'YTD'] as const

export default function InvoicingPage() {
  const navigate = useNavigate()
  const [tab,    setTab]    = useState<WorklistTab>('all')
  const [period, setPeriod] = useState<typeof SEG_PERIODS[number]>('May')
  const [checked, setChecked] = useState<Set<string>>(
    new Set(WORKLIST.filter(r => r.primary || r.status === 'sent' || ['EX', 'KL', 'PO'].includes(r.customerKey)).map(r => r.customerKey)),
  )

  const toggleAll = () => {
    if (checked.size === filteredRows.length) setChecked(new Set())
    else setChecked(new Set(filteredRows.map(r => r.customerKey)))
  }
  const toggleOne = (key: string) => {
    const next = new Set(checked)
    if (next.has(key)) next.delete(key); else next.add(key)
    setChecked(next)
  }

  const filteredRows = WORKLIST.filter(r => {
    if (tab === 'all')     return true
    if (tab === 'flagged') return r.flagged
    return r.status === tab
  })

  const selectedRows = filteredRows.filter(r => checked.has(r.customerKey))
  const selectedSum  = selectedRows.reduce((s, r) => s + r.mayKsek, 0)

  const goToComposer = () => navigate({ to: '/invoicing/composer' })

  // Stacked progress bar segments (each = % of target).
  const segPart = (val: number, color: string, title: string) => (
    <div
      title={title}
      style={{
        width: `${(val / RUN.targetKsek) * 100}%`,
        background: color,
        height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        padding: '0 8px',
        color: 'white', fontWeight: 600, fontSize: 11.5,
        fontVariantNumeric: 'tabular-nums',
        borderRight: '2px solid var(--ns-surface)',
      }}
    >
      {val}
    </div>
  )

  return (
    <div className="page">

      {/* Page header */}
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Invoicing</div>
          <h1 className="page-title">Month-end invoicing · {RUN.monthLabel}</h1>
          <div className="page-subtitle">
            All time registered this month should be billed. Working day {RUN.workingDay} of {RUN.workingDays} · run closes {RUN.closeDate}.
          </div>
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <div className="seg" role="tablist">
            {SEG_PERIODS.map(p => (
              <button
                key={p}
                className={period === p ? 'active' : ''}
                onClick={() => setPeriod(p)}
              >{p}</button>
            ))}
          </div>
          <button className="btn">
            <Download size={13} /> Export
          </button>
          <button className="btn btn-brand" onClick={goToComposer}>
            <Check size={13} /> Start invoice run
          </button>
        </div>
      </div>

      {/* HERO: month-end run progress */}
      <section
        className="card"
        style={{
          padding: '20px 22px', marginBottom: 14,
          display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 28,
        }}
      >
        {/* Left: progress */}
        <div style={{ minWidth: 0 }}>
          <div className="row" style={{ alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <span style={{
              fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>Left to invoice · {RUN.monthLabel.split(' ')[0]}</span>
            <span style={{ fontSize: 12, color: 'var(--ns-text-subtle)', fontWeight: 500 }}>
              Target <b style={{ color: 'var(--ns-text)' }}>{fmtKsek(RUN.targetKsek)} kSEK</b> · invoiced so far <b style={{ color: 'var(--ns-text)' }}>{fmtKsek(RUN.invoicedKsek)} kSEK</b> · <b style={{ color: 'var(--ns-text)' }}>{RUN.pendingInvoices} invoices</b> pending
            </span>
          </div>

          <div className="row" style={{ alignItems: 'baseline', gap: 16, marginBottom: 12, fontVariantNumeric: 'tabular-nums' }}>
            <span style={{ fontSize: 40, fontWeight: 600, letterSpacing: '-0.02em' }}>
              {fmtKsek(RUN.leftKsek)}
              <span style={{ fontSize: 16, color: 'var(--ns-text-muted)', fontWeight: 500, marginLeft: 4 }}>kSEK</span>
            </span>
            <span style={{ fontSize: 13, color: 'var(--ns-text-muted)' }}>
              of {fmtKsek(RUN.targetKsek)} kSEK · <b style={{ color: 'var(--ns-text)' }}>{pctOf(RUN.invoicedKsek, RUN.targetKsek)}%</b> complete
            </span>
            <span style={{
              display: 'inline-flex', alignItems: 'baseline',
              fontSize: 13, fontWeight: 600,
              color: 'var(--ns-ok)', background: 'var(--ns-ok-soft)',
              padding: '3px 10px', borderRadius: 999,
            }}>
              <span style={{ fontSize: 16, marginRight: 4, fontVariantNumeric: 'tabular-nums' }}>+{RUN.paceDelta}%</span>
              ahead of pacing
            </span>
          </div>

          {/* Stacked progress bar */}
          <div
            aria-label="Invoiced vs target, by category"
            style={{
              position: 'relative', height: 30, borderRadius: 6, overflow: 'hidden',
              background: 'var(--ns-surface-3)', display: 'flex',
              boxShadow: 'inset 0 0 0 1px var(--ns-border)',
            }}
          >
            {segPart(CATEGORIES[0].invoiced, CAT_COLORS.time,     `Time-based ${CATEGORIES[0].invoiced}/${CATEGORIES[0].target}`)}
            {segPart(CATEGORIES[1].invoiced, CAT_COLORS.contract, `Contracts ${CATEGORIES[1].invoiced}/${CATEGORIES[1].target}`)}
            {segPart(CATEGORIES[2].invoiced, CAT_COLORS.product,  `Clementine ${CATEGORIES[2].invoiced}/${CATEGORIES[2].target}`)}
            {segPart(CATEGORIES[3].invoiced, CAT_COLORS.license,  `Licenses ${CATEGORIES[3].invoiced}/${CATEGORIES[3].target}`)}
            <div style={{
              flex: 1,
              display: 'flex', alignItems: 'center',
              padding: '0 10px',
              color: 'var(--ns-text-muted)', fontSize: 11.5, fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {fmtKsek(RUN.leftKsek)} kSEK to go
            </div>
          </div>

          {/* Legend */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '14px 22px',
            marginTop: 18,
          }}>
            {CATEGORIES.map(c => (
              <div key={c.key} style={{
                display: 'grid',
                gridTemplateColumns: '10px auto',
                columnGap: 8, rowGap: 1,
                fontVariantNumeric: 'tabular-nums',
              }}>
                <span style={{
                  gridRow: '1 / 3',
                  width: 10, height: 10, borderRadius: 2,
                  marginTop: 4, background: CAT_COLORS[c.key],
                }} />
                <span style={{ fontSize: 11.5, color: 'var(--ns-text-muted)', fontWeight: 500 }}>
                  {c.label}
                </span>
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>
                  {fmtKsek(c.invoiced)}
                  <span style={{ color: 'var(--ns-text-muted)', fontWeight: 500 }}> / {fmtKsek(c.target)} kSEK</span>
                </span>
                <div style={{
                  gridColumn: 2,
                  height: 3, borderRadius: 2, overflow: 'hidden',
                  background: 'var(--ns-surface-3)',
                  marginTop: 4, width: 140,
                }}>
                  <div style={{ height: '100%', width: `${c.pct}%`, background: CAT_COLORS[c.key] }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: countdown + run summary + CTAs */}
        <div style={{
          borderLeft: '1px solid var(--ns-border)',
          paddingLeft: 28,
          display: 'flex', flexDirection: 'column', gap: 14,
        }}>
          <div className="row" style={{ alignItems: 'baseline', gap: 10 }}>
            <span style={{
              fontSize: 32, fontWeight: 600, letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
            }}>{RUN.daysToClose}</span>
            <span style={{ fontSize: 12.5, color: 'var(--ns-text-muted)', fontWeight: 500 }}>
              working days to <b style={{ color: 'var(--ns-text)' }}>month close</b>
              <br />Run window: {RUN.runWindow}
            </span>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1,
            background: 'var(--ns-border)',
            border: '1px solid var(--ns-border)',
            borderRadius: 8, overflow: 'hidden',
          }}>
            <RunStat num={RUN.pendingInvoices}  unit="invoices"  label="Pending creation" />
            <RunStat num={RUN.customersAction}  unit="customers" label="Need action" />
            <RunStat num={RUN.redFlags}         unit="acct."     label="Red flag · no hours / no inv" tone="danger" />
            <RunStat num={RUN.draftsInReview}   unit="drafts"    label="In review · awaiting PM sign-off" tone="warn" />
          </div>

          <div className="row" style={{ gap: 8, marginTop: 'auto' }}>
            <button
              className="btn btn-brand"
              style={{ flex: 1, justifyContent: 'center' }}
              onClick={goToComposer}
            >
              <Check size={13} /> Generate ready invoices ({CATEGORIES.reduce((s, c) => s + (c.invoices), 0) - 7 /* sent + holds */})
            </button>
            <button className="btn">
              <Calendar size={13} /> Schedule
            </button>
          </div>
        </div>
      </section>

      {/* Category cards */}
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
        {CATEGORIES.map(c => {
          const deltaUp = c.delta >= 0
          return (
            <div
              key={c.key}
              className="card"
              style={{
                padding: '14px 16px',
                borderTop: `3px solid ${CAT_COLORS[c.key]}`,
              }}
            >
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <span style={{
                  fontSize: 11, color: 'var(--ns-text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
                }}>
                  {c.label} <b style={{ color: 'var(--ns-text)', fontWeight: 600 }}>· {c.sub}</b>
                </span>
                <span style={{
                  fontSize: 11.5, fontWeight: 600,
                  color: 'var(--ns-text-muted)',
                  fontVariantNumeric: 'tabular-nums',
                }}>{c.pct}%</span>
              </div>
              <div className="row" style={{ alignItems: 'baseline', gap: 6, fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}>
                  {fmtKsek(c.invoiced)}
                </span>
                <span style={{ fontSize: 12.5, color: 'var(--ns-text-muted)', fontWeight: 500 }}>
                  / {fmtKsek(c.target)} kSEK
                </span>
              </div>
              <div style={{
                height: 6, borderRadius: 3, overflow: 'hidden',
                background: 'var(--ns-surface-3)',
                marginTop: 10,
              }}>
                <div style={{ height: '100%', width: `${c.pct}%`, background: CAT_COLORS[c.key] }} />
              </div>
              <div className="row" style={{ justifyContent: 'space-between', fontSize: 11.5, color: 'var(--ns-text-muted)', marginTop: 8 }}>
                <span><b style={{ color: 'var(--ns-text)', fontWeight: 600 }}>{c.left} kSEK</b> left · {c.invoices} invoice{c.invoices === 1 ? '' : 's'}</span>
                <span style={{ color: deltaUp ? 'var(--ns-ok)' : 'var(--ns-err)', fontWeight: 600 }}>
                  {deltaUp ? '↑' : '↓'} {Math.abs(c.delta)}% vs Apr
                </span>
              </div>
            </div>
          )
        })}
      </section>

      {/* PM leaderboard + Flags */}
      <section style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14, marginBottom: 14 }}>
        {/* PM leaderboard */}
        <div className="card">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', padding: '14px 18px', borderBottom: '1px solid var(--ns-border)' }}>
            <div>
              <div style={{
                fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>Left to invoice · by project manager</div>
              <div style={{ fontSize: 12, color: 'var(--ns-text-subtle)', marginTop: 2 }}>
                PM is responsible for review &amp; sign-off on each invoice
              </div>
            </div>
            <button
              className="btn btn-ghost"
              style={{ fontSize: 12, color: 'var(--ns-brand-strong)', fontWeight: 500 }}
            >All PMs →</button>
          </div>

          <div>
            {PMS.map(pm => (
              <div
                key={pm.initials}
                className="row"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '32px minmax(0, 1fr) 1.6fr 88px 64px',
                  alignItems: 'center', gap: 14,
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--ns-border)',
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  color: 'white', fontSize: 11, fontWeight: 600,
                  display: 'grid', placeItems: 'center',
                  background: pm.av,
                }}>{pm.initials}</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{pm.name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--ns-text-subtle)', marginTop: 1 }}>
                    {pm.customers} customers · {pm.invoices} invoices pending
                    {pm.flagged > 0 && (
                      <span style={{ color: 'var(--ns-err)', marginLeft: 4, fontWeight: 600 }}>● {pm.flagged} flagged</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="row" style={{ gap: 8, fontSize: 11.5, color: 'var(--ns-text-subtle)', fontVariantNumeric: 'tabular-nums' }}>
                    <span style={{ fontWeight: 600, color: 'var(--ns-text)', fontSize: 12.5, minWidth: 86 }}>
                      {fmtKsek(pm.invoicedKsek)} / {fmtKsek(pm.totalKsek)} kSEK
                    </span>
                    <div style={{
                      flex: 1, height: 8, borderRadius: 4, overflow: 'hidden',
                      background: 'var(--ns-surface-3)', display: 'flex',
                    }}>
                      <PmSegments segments={pm.segments} remaining={pm.remaining} />
                    </div>
                  </div>
                </div>
                <div style={{
                  textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                  fontWeight: 600, fontSize: 14,
                }}>
                  {fmtKsek(pm.leftKsek)}<span style={{ fontSize: 11, color: 'var(--ns-text-subtle)', fontWeight: 500, marginLeft: 1 }}>kSEK</span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--ns-text-subtle)', fontWeight: 500, marginTop: 1 }}>
                    left · {pctOf(pm.leftKsek, pm.totalKsek)}%
                  </span>
                </div>
                <button
                  className="btn"
                  style={{ padding: '6px 8px', fontSize: 11.5 }}
                  onClick={goToComposer}
                >Review</button>
              </div>
            ))}
          </div>

          <div className="row" style={{
            padding: '10px 18px', borderTop: '1px solid var(--ns-border)',
            fontSize: 11.5, color: 'var(--ns-text-subtle)',
            gap: 14, alignItems: 'center',
          }}>
            {(Object.keys(CAT_COLORS) as CategoryKey[]).map(k => (
              <span key={k}>
                <span style={{
                  display: 'inline-block', width: 8, height: 8, borderRadius: 2,
                  background: CAT_COLORS[k], verticalAlign: 1, marginRight: 4,
                }} />
                {CAT_LABELS[k]}
              </span>
            ))}
            <span style={{ marginLeft: 'auto' }}>Solid = invoiced · faded = left to invoice</span>
          </div>
        </div>

        {/* Red flags */}
        <div className="card">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', padding: '14px 18px', borderBottom: '1px solid var(--ns-border)' }}>
            <div>
              <div style={{
                fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>Red flags · vs previous month</div>
              <div style={{ fontSize: 12, color: 'var(--ns-text-subtle)', marginTop: 2 }}>
                Active accounts without hours or without invoice
              </div>
            </div>
            <button
              className="btn btn-ghost"
              style={{ fontSize: 12, color: 'var(--ns-brand-strong)', fontWeight: 500 }}
            >Full audit →</button>
          </div>

          <div>
            {FLAGS.map(f => (
              <div
                key={f.title + f.body}
                style={{
                  display: 'grid', gridTemplateColumns: '22px 1fr auto',
                  gap: 10, alignItems: 'center',
                  padding: '10px 14px',
                  borderBottom: '1px solid var(--ns-border)',
                  cursor: 'pointer',
                }}
              >
                <FlagIcon severity={f.severity} />
                <div style={{ fontSize: 12.5, lineHeight: 1.4 }}>
                  <b style={{ fontWeight: 600 }}>{f.title}</b> · {f.body}
                  <div style={{ color: 'var(--ns-text-subtle)', fontSize: 11.5, marginTop: 1 }}>{f.secondary}</div>
                </div>
                <span style={{
                  fontSize: 11.5, color: 'var(--ns-brand-strong)', fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}>{f.action}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Worklist */}
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', margin: '22px 0 10px' }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Invoice worklist · {RUN.monthLabel}</h2>
        <span style={{ fontSize: 12, color: 'var(--ns-text-subtle)' }}>
          All customers with billable activity this month — sorted by readiness
        </span>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        {/* Toolbar */}
        <div
          className="row"
          style={{
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid var(--ns-border)',
            background: 'var(--ns-surface-2)',
          }}
        >
          <div className="row" style={{ gap: 16, alignItems: 'center' }}>
            <span style={{
              fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>Invoices</span>
            <span style={{ fontSize: 12, color: 'var(--ns-text-subtle)' }}>
              {WORKLIST.filter(r => r.status !== 'sent').length} pending · {WORKLIST.filter(r => r.status === 'sent').length} sent · {WORKLIST.filter(r => r.flagged).length} flagged
            </span>
            <div className="row" style={{ gap: 0 }}>
              {WORKLIST_TABS.map(t => (
                <button
                  key={t.key}
                  className={`btn btn-ghost`}
                  onClick={() => setTab(t.key)}
                  style={{
                    padding: tab === t.key ? '4px 10px' : '5px 11px',
                    fontSize: 12,
                    color: tab === t.key ? 'var(--ns-text)' : 'var(--ns-text-muted)',
                    background: tab === t.key ? 'var(--ns-surface)' : 'transparent',
                    border: tab === t.key ? '1px solid var(--ns-border-strong)' : '1px solid transparent',
                    fontWeight: 500,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {t.label}
                  <span style={{
                    fontSize: 10.5, fontWeight: 600,
                    padding: '1px 6px', borderRadius: 999,
                    background: 'danger' in t && t.danger
                      ? 'var(--ns-err-soft)'
                      : tab === t.key ? 'var(--ns-brand-soft)' : 'var(--ns-surface-3)',
                    color: 'danger' in t && t.danger
                      ? 'var(--ns-err)'
                      : tab === t.key ? 'var(--ns-brand-strong)' : 'var(--ns-text-muted)',
                  }}>{t.count}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="row" style={{ gap: 6, alignItems: 'center' }}>
            <button className="btn"><Filter size={13} /> Filter</button>
            <button className="btn btn-icon" title="Density"><Rows3 size={13} /></button>
            <button className="btn btn-icon" title="Columns"><Columns3 size={13} /></button>
          </div>
        </div>

        {/* Table */}
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <Checkbox
                  on={filteredRows.length > 0 && filteredRows.every(r => checked.has(r.customerKey))}
                  onClick={toggleAll}
                />
              </th>
              <th style={{ minWidth: 220 }}>Customer</th>
              <th>Project manager</th>
              <th>Billing category</th>
              <th style={{ textAlign: 'right' }}>Apr<br />invoiced</th>
              <th style={{ textAlign: 'right' }}>May<br />amount</th>
              <th style={{ textAlign: 'center' }}>vs Apr</th>
              <th style={{ textAlign: 'center' }}>Status</th>
              <th style={{ textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map(r => (
              <tr
                key={r.customerKey}
                onClick={() => toggleOne(r.customerKey)}
                style={{
                  background: r.flagged
                    ? 'color-mix(in oklch, var(--ns-err-soft) 60%, var(--ns-surface))'
                    : undefined,
                  cursor: 'pointer',
                }}
              >
                <td onClick={e => { e.stopPropagation(); toggleOne(r.customerKey) }}>
                  <Checkbox on={checked.has(r.customerKey)} onClick={() => toggleOne(r.customerKey)} />
                </td>
                <td>
                  <div className="row" style={{ gap: 10 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 6,
                      color: r.phColor ?? 'white',
                      display: 'grid', placeItems: 'center',
                      fontSize: 10, fontWeight: 700,
                      flexShrink: 0,
                      background: r.ph,
                    }}>{r.customerKey}</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13.5 }}>{r.customer}</div>
                      <div style={{ fontSize: 11, color: 'var(--ns-text-subtle)', marginTop: 1 }}>{r.project}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="row" style={{ gap: 8 }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%',
                      color: 'white', fontSize: 9, fontWeight: 600,
                      display: 'grid', placeItems: 'center',
                      background: r.pm.av,
                    }}>{r.pm.initials}</div>
                    <span style={{ fontSize: 12 }}>{r.pm.name}</span>
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {r.categories.map(c => <CatPill key={c} cat={c} />)}
                  </div>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span style={{ fontWeight: 500, fontSize: 13.5, color: 'var(--ns-text-muted)' }}>
                    {fmtKsek(r.aprKsek)}<span style={{ fontSize: 11, color: 'var(--ns-text-subtle)', fontWeight: 500, marginLeft: 1 }}> kSEK</span>
                  </span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span style={{
                    fontWeight: 600, fontSize: 13.5,
                    color: r.flagged && r.mayKsek === 0 ? 'var(--ns-err)' : undefined,
                  }}>
                    {fmtKsek(r.mayKsek)}<span style={{ fontSize: 11, color: 'var(--ns-text-subtle)', fontWeight: 500, marginLeft: 1 }}> kSEK</span>
                  </span>
                </td>
                <td style={{ textAlign: 'center' }}>
                  {r.noteRow
                    ? <span style={{ color: 'var(--ns-text-muted)', fontSize: 11.5, fontWeight: 600 }}>{r.noteRow}</span>
                    : <DeltaCell pct={r.deltaPct} />
                  }
                </td>
                <td style={{ textAlign: 'center' }}>
                  <StatusPill status={r.status} label={r.statusLabel} />
                </td>
                <td style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                  <button
                    className={r.primary ? 'btn btn-brand' : 'btn'}
                    style={{ padding: '5px 10px', fontSize: 12 }}
                    onClick={goToComposer}
                  >
                    {r.action}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Footer */}
        <div
          className="row"
          style={{
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderTop: '1px solid var(--ns-border)',
            background: 'var(--ns-surface-2)',
          }}
        >
          <div style={{ fontSize: 12, color: 'var(--ns-text-subtle)' }}>
            <b style={{ color: 'var(--ns-text)', fontWeight: 600 }}>{selectedRows.length} selected</b>
            {' · '}
            {fmtKsek(selectedSum)} kSEK · {new Set(selectedRows.map(r => r.customerKey)).size} customer{selectedRows.length === 1 ? '' : 's'}
          </div>
          <div className="row" style={{ gap: 6 }}>
            <button className="btn"><ListChecks size={13} /> Mark as ready</button>
            <button className="btn"><Download size={13} /> Export selection</button>
            <button
              className="btn btn-brand"
              onClick={goToComposer}
              disabled={selectedRows.length === 0}
            >
              <Check size={13} /> Generate {selectedRows.length} invoice{selectedRows.length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Subcomponents ────────────────────────────────────────────────────────────

function RunStat({ num, unit, label, tone }: { num: number; unit: string; label: string; tone?: 'danger' | 'warn' }) {
  const color = tone === 'danger' ? 'var(--ns-err)' : tone === 'warn' ? 'var(--ns-warn)' : undefined
  return (
    <div style={{ background: 'var(--ns-surface)', padding: '10px 12px' }}>
      <div style={{
        fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em',
        fontVariantNumeric: 'tabular-nums',
        color,
      }}>
        {num}<span style={{ fontSize: 12, color: 'var(--ns-text-muted)', fontWeight: 500, marginLeft: 2 }}>{unit}</span>
      </div>
      <div style={{
        fontSize: 10.5, fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.05em',
        marginTop: 2,
        color: color ?? 'var(--ns-text-subtle)',
      }}>{label}</div>
    </div>
  )
}

function FlagIcon({ severity }: { severity: FlagSeverity }) {
  const map = {
    danger: { bg: 'var(--ns-err-soft)',   color: 'var(--ns-err)',   Icon: AlertOctagon },
    warn:   { bg: 'var(--ns-warn-soft)',  color: 'var(--ns-warn)',  Icon: AlertTriangle },
    info:   { bg: 'var(--ns-brand-soft)', color: 'var(--ns-brand-strong)', Icon: Info },
  } as const
  const { bg, color, Icon } = map[severity]
  return (
    <span style={{
      width: 22, height: 22, borderRadius: '50%',
      display: 'grid', placeItems: 'center',
      background: bg, color,
      flexShrink: 0,
    }}>
      <Icon size={12} />
    </span>
  )
}

function PmSegments({
  segments, remaining,
}: {
  segments:  Record<CategoryKey, number>
  remaining: Record<CategoryKey, number>
}) {
  const cats: CategoryKey[] = ['time', 'contract', 'product', 'license']
  return (
    <>
      {cats.map(c => segments[c] > 0 && (
        <div key={`solid-${c}`} style={{ width: `${segments[c]}%`, background: CAT_COLORS[c] }} />
      ))}
      {cats.map(c => remaining[c] > 0 && (
        <div
          key={`fade-${c}`}
          style={{
            width: `${remaining[c]}%`,
            background: `color-mix(in oklch, ${CAT_COLORS[c]} 35%, var(--ns-surface))`,
          }}
        />
      ))}
    </>
  )
}
